import React, { useEffect, useRef, useState } from 'react';
import { Copy, ExternalLink, QrCode, Download, Upload, Trash2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { cardsAPI } from '@/lib/cardsAPI';
import { CardView } from './PublicCard';

const red = 'bg-[#A62B1F] hover:bg-[#8C2318] text-white';
const box = 'rounded-xl border border-[#E3DDD1] bg-white p-4';
const err = (e, f) => toast.error(e?.response?.data?.detail || f);
const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob); const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 2000);
};

export default function MyCard() {
  const [card, setCard] = useState(null);
  const [f, setF] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [qr, setQr] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const loadPhoto = () => cardsAPI.photo().then((r) => setPhoto(URL.createObjectURL(r.data))).catch(() => setPhoto(null));
  useEffect(() => {
    cardsAPI.me().then((r) => { setCard(r.data); setF(r.data); if (r.data.has_photo) loadPhoto(); if (r.data.qr_code_id) cardsAPI.makeQr().then((q) => setQr(q.data)); })
      .catch((e) => err(e, 'Could not load your card'));
  }, []);
  if (!f) return <Layout title="My card"><p className="text-sm text-[#5C6670]">Loading…</p></Layout>;

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const r = await cardsAPI.save({ slug: f.slug, full_name: f.full_name, title: f.title, org: f.org, email: f.email, phone_office: f.phone_office,
        phone_mobile: f.phone_mobile, website: f.website, address: f.address, bio: f.bio, accent: f.accent, links: f.links, ...extra });
      setCard(r.data); setF(r.data); toast.success(extra.published === true ? 'Your card is live' : extra.published === false ? 'Your card is hidden' : 'Saved');
    } catch (e) { err(e, 'Could not save'); } finally { setSaving(false); }
  };

  return (
    <Layout title="My card">
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#1E2A33]" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>My card</h1>
          <p className="text-sm text-[#5C6670]">Your profile page for the QR code on your printed business card. Anyone who scans it can save you to their phone in one tap.</p>
        </div>
        <div className="grid lg:grid-cols-[1fr_380px] gap-4 items-start">
          <div className="space-y-4">
            <div className={`${box} flex items-center justify-between gap-3 flex-wrap`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold">{card.published ? 'Your card is live' : 'Your card is hidden'}</div>
                <code className="text-xs text-[#5C6670] break-all">{card.url}</code>
              </div>
              <div className="flex items-center gap-2">
                {card.published && <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(card.url); toast.success('Link copied'); }}><Copy className="w-4 h-4" /></Button>}
                {card.published && <Button size="sm" variant="outline" onClick={() => window.open(card.url, '_blank')}><ExternalLink className="w-4 h-4" /></Button>}
                <Switch checked={card.published} disabled={saving} onCheckedChange={(v) => save({ published: v })} />
              </div>
            </div>

            <div className={`${box} space-y-3`}>
              <div className="flex items-center gap-3">
                {photo ? <img src={photo} alt="" className="w-16 h-16 rounded-full object-cover" /> : <div className="w-16 h-16 rounded-full bg-[#F3EFE7]" />}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  try { await cardsAPI.uploadPhoto(file); await loadPhoto(); setCard({ ...card, has_photo: true }); toast.success('Photo saved'); } catch (er) { err(er, 'Could not upload'); }
                  e.target.value = '';
                }} />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}><Upload className="w-4 h-4 mr-1" />{photo ? 'Change photo' : 'Add a photo'}</Button>
                {photo && <Button size="sm" variant="ghost" className="text-[#A62B1F]" onClick={async () => { await cardsAPI.removePhoto(); setPhoto(null); setCard({ ...card, has_photo: false }); }}><Trash2 className="w-4 h-4" /></Button>}
              </div>
              <p className="text-xs text-[#5C6670]">Your photo is saved with your contact, so it shows when you call or email.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={f.full_name} onChange={set('full_name')} /></div>
                <div><Label>Title</Label><Input value={f.title} onChange={set('title')} placeholder="Founder & Builder" /></div>
                <div><Label>Company</Label><Input value={f.org} onChange={set('org')} /></div>
                <div><Label>Email</Label><Input value={f.email} onChange={set('email')} /></div>
                <div><Label>Cell</Label><Input value={f.phone_mobile} onChange={set('phone_mobile')} placeholder="1-437-000-0000" /></div>
                <div><Label>Office</Label><Input value={f.phone_office} onChange={set('phone_office')} /></div>
                <div><Label>Website</Label><Input value={f.website} onChange={set('website')} placeholder="stageheadgroup.com" /></div>
                <div><Label>Location</Label><Input value={f.address} onChange={set('address')} placeholder="Toronto, ON" /></div>
              </div>
              <div><Label>A line about you (optional)</Label><Textarea rows={3} value={f.bio} onChange={set('bio')} /></div>
              <div>
                <Label>Other links (optional)</Label>
                <div className="space-y-2 mt-1">
                  {(f.links || []).map((l, i) => (
                    <div key={i} className="grid grid-cols-[120px_1fr_32px] gap-2">
                      <Input placeholder="LinkedIn" value={l.label} onChange={(e) => setF({ ...f, links: f.links.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                      <Input placeholder="linkedin.com/in/…" value={l.url} onChange={(e) => setF({ ...f, links: f.links.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) })} />
                      <button className="text-[#5C6670]" onClick={() => setF({ ...f, links: f.links.filter((_, j) => j !== i) })}><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {(f.links || []).length < 8 && <button className="text-xs font-semibold text-[#A62B1F] inline-flex items-center gap-1" onClick={() => setF({ ...f, links: [...(f.links || []), { label: '', url: '' }] })}><Plus className="w-3 h-3" />Add a link</button>}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Page address</Label>
                  <div className="flex items-center gap-1 text-sm"><span className="text-[#5C6670] shrink-0">/c/</span><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase() })} /></div></div>
                <div><Label>Button colour</Label>
                  <div className="flex items-center gap-2"><input type="color" value={f.accent || '#A62B1F'} onChange={set('accent')} className="h-10 w-12 rounded border border-[#E3DDD1]" /><Input value={f.accent} onChange={set('accent')} /></div></div>
              </div>
              <Button className={red} disabled={saving} onClick={() => save()}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>

            <div className={`${box} space-y-3`}>
              <div className="flex items-center gap-2"><QrCode className="w-4 h-4 text-[#A62B1F]" /><span className="font-semibold text-sm">QR code for your printed card</span></div>
              <p className="text-xs text-[#5C6670]">The code goes through a Stagehead link, so you can change your page later without reprinting, and you can see how many times it's been scanned.</p>
              {!qr ? <Button variant="outline" onClick={async () => { try { const r = await cardsAPI.makeQr(); setQr(r.data); } catch (e) { err(e, 'Could not make the code'); } }}>Make my QR code</Button> : (
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" onClick={async () => saveBlob((await cardsAPI.downloadQr('svg')).data, 'my-card-qr.svg')}><Download className="w-4 h-4 mr-1" />SVG for the printer</Button>
                  <Button variant="outline" onClick={async () => saveBlob((await cardsAPI.downloadQr('png')).data, 'my-card-qr.png')}><Download className="w-4 h-4 mr-1" />PNG</Button>
                  <span className="text-xs text-[#5C6670]">{qr.scans} scan{qr.scans === 1 ? '' : 's'} · {card.stats?.views || 0} page views · {card.stats?.saves || 0} contacts saved</span>
                </div>
              )}
              {!card.published && qr && <p className="text-xs text-amber-900">Switch your card on above before you print, or the code will lead to an empty page.</p>}
            </div>
          </div>

          <div className="rounded-[2rem] border-8 border-[#1E2A33] overflow-hidden bg-[#FBF8F2] lg:sticky lg:top-4 max-h-[780px] overflow-y-auto">
            <CardView card={f} photoSrc={photo} preview onSave={() => {}} />
          </div>
        </div>
      </div>
    </Layout>
  );
}
